import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RiSendPlaneFill } from '@remixicon/react';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { apiClient, freshQuoteNumber } from '@/lib/api';
import { buildInvoiceEmailHtml } from '@/utils/emailTemplates';

const TAX_RATE = 0.10;

const AdvanceInvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const { userProfile } = useAuthStore();

  const { data: organization } = useQuery({
      queryKey: ["organization", userProfile?.org_id],
      queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
      enabled: !!userProfile?.org_id,
  });

  const clientName = location.state?.clientName || '';

  const [lineItem, setLineItem] = useState('');
  const [itemCost, setItemCost] = useState('');
  const [taxPercent, setTaxPercent] = useState(String(TAX_RATE * 100));
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setPageHeaderOverride({
      title: 'Advance Invoice',
      subtitle: clientName || undefined,
    });
    return () => clearPageHeaderOverride();
  }, [clientName, setPageHeaderOverride, clearPageHeaderOverride]);

  const formatWithCommas = (val) => {
    const stripped = val.replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = stripped.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.slice(0, 2).join(stripped.includes('.') ? '.' : '');
  };

  const parseAmount = (val) => parseFloat((val || '').replace(/,/g, '')) || 0;

  const handleItemCostChange = (val) => {
    setItemCost(formatWithCommas(val));
  };

  const handleTaxPercentChange = (val) => {
    const stripped = val.replace(/[^\d.]/g, '');
    const parts = stripped.split('.');
    setTaxPercent(parts.slice(0, 2).join(stripped.includes('.') ? '.' : ''));
  };

  const taxAmount = parseAmount(itemCost) * ((parseFloat(taxPercent) || 0) / 100);
  const formattedTaxAmount = taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isValid = lineItem.trim() && parseAmount(itemCost) > 0;

  const handleSend = async () => {
    if (!isValid || isSending) return;
    setIsSending(true);
    try {

      const newQuoteNumber = freshQuoteNumber();
      const totalAmount = parseAmount(itemCost) + taxAmount;
      const invoiceItems = [{ item: lineItem.trim(), amount: parseAmount(itemCost), tax_amount: taxAmount, line_total: parseAmount(itemCost) + taxAmount }];
      const invoice = await apiClient.createInvoice({
        broker_org_id: userProfile?.org_id || null,
        created_by: userProfile?.userprofile_id || null,
        client_id: id,
        quote_id:  null,
        flight_id: null,
        invoice_type: 'ADVANCE',
        subtotal: parseAmount(itemCost),
        tax_amount: taxAmount,
        platform_fee_percent: 0,
        platform_fee: 0,
        total_amount: totalAmount,
        invoice_number: newQuoteNumber,
        items: invoiceItems,
        status: 'SENT',
      });

      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      // Fire-and-forget: fetch client email then send notification
      apiClient.getClientById(id).then(async client => {
        if (!client?.email) return;

        // New checkout token tied to the (possibly new) total amount.
        const requestToken = await apiClient.createCheckoutToken({
          quote_id: invoice.invoice_id,
          amount: parseAmount(itemCost) + taxAmount || 0,
          currency: 'USD',
          payment_method: 'CREDIT_CARD',
          description: 'Charter flight quote payment',
          metadata: {
            customer_name: [
              client.first_name,
              client.last_name,
            ].filter(Boolean).join(' '),
            customer_email: client.email,
          },
        });

        const checkoutLink = `${window.location.origin}/checkout?invoiceid=${invoice.invoice_id}&token=${requestToken?.token}&option=invoiceoneoff`;

        const emailHtml = buildInvoiceEmailHtml({
          clientName,
          services: invoiceItems,
          itemCost: parseAmount(itemCost),
          total: parseAmount(itemCost) + taxAmount,
          quoteNumber: newQuoteNumber,
          oneOff: true,
          checkoutUrl: checkoutLink,
          trustjetFee: 0,
          taxAmount,
          organization
        });
        return apiClient.sendMessage({
          From: 'tools@trustjet.ai',
          To: client.email,
          Cc: userProfile?.email || '',
          ReplyTo: userProfile?.email || '',
          Subject: `TrustJet - Advance Invoice (${newQuoteNumber})`,
          HtmlBody: emailHtml,
        });
      }).catch(err => console.error('Failed to send invoice email:', err));

      toast.success('Invoice created successfully');
      navigate(-1);
    } catch (err) {
      console.error('Failed to create invoice:', err);
      toast.error('Failed to create invoice');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="pb-24 w-full lg:max-w-[1000px] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <main className="py-3">
        <div className="space-y-4">

          {/* Form Card */}
          <div className="space-y-5 overflow-hidden flex flex-col h-[calc(100vh-250px)] px-1">

            {/* Line Item */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Line Item <span className="text-primary-500">*</span>
              </label>
              <textarea
                value={lineItem}
                onChange={(e) => setLineItem(e.target.value)}
                placeholder="Add specific details"
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Item Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item Cost <span className="text-primary-500">*</span>
              </label>
              <div className="flex bg-white items-center border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all">
                <span className="pl-4 pr-2 text-sm text-gray-400 font-medium select-none">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={itemCost}
                  onChange={(e) => handleItemCostChange(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 bg-white py-3 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                />
              </div>
            </div>

            {/* Taxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taxes(%) <span className="text-primary-500">*</span>
              </label>
              <div className="flex bg-white items-center border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all">
                <input
                  type="text"
                  inputMode="decimal"
                  value={taxPercent}
                  onChange={(e) => handleTaxPercentChange(e.target.value)}
                  placeholder="10"
                  className="flex-1 bg-white pl-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                />
                <span className="pr-4 pl-2 text-sm text-gray-400 font-medium select-none">%</span>
              </div>
              <p className="text-sm text-gray-600 mt-1.5"><strong>${formattedTaxAmount}</strong> in taxes</p>
            </div>
          </div>

          {/* Total + Send Button */}
          <div className="shrink-0">
          {(parseAmount(itemCost) > 0 || taxAmount > 0) && (
            <div className="flex items-center justify-between px-1 mb-3">
              <span className="text-sm text-gray-500">Total Cost</span>
              <span className="text-sm font-semibold text-gray-900">
                ${(parseAmount(itemCost) + taxAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
          <button
            onClick={handleSend}
            disabled={!isValid || isSending}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-full text-sm transition-all ${
              isValid && !isSending
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <RiSendPlaneFill className="w-4 h-4" />
            )}
            Send Invoice
          </button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default AdvanceInvoicePage;
